import { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import DerasarSelector from './DerasarSelector';
import FinancialYearSelector from './FinancialYearSelector';
import ThemeSelector from '../ThemeSelector';
import NotificationBell from '../notifications/NotificationBell';
import { useAuth } from '../../context/AuthContext';
import { FiMenu, FiUser, FiLogOut, FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi';
import styles from './AppLayout.module.css';

const roleLabelMap = {
  super_admin: 'Super Admin',
  platform_admin: 'Platform Admin',
  derasar_admin: 'Derasar Admin',
  user: 'User',
};

const getInitials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const {
    user,
    logout,
    isDerasarAdmin,
    activeDerasar,
    isActiveDerasarExpired,
    isActiveDerasarExpiringSoon,
    activeDerasarHasPendingRenewal,
    isActiveDerasarTrial,
    activeDerasarSubscriptionEndDate,
    activeDerasarDaysUntilExpiry,
  } = useAuth();

  // DerasarSelector is hidden for derasar_admin — show name tag only for them
  const activeDerasarName = user?.role === 'derasar_admin' ? (activeDerasar?.derasarId?.name || null) : null;
  const navigate = useNavigate();
  const avatarRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleProfile = () => { setAvatarOpen(false); navigate('/profile'); };
  const handleLogout  = () => { setAvatarOpen(false); logout(); };

  return (
    <div className={styles.layout}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
      />
      <div className={`${styles.mainContent} ${sidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
        <header className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <button className={styles.menuBtn} onClick={() => setSidebarOpen(true)}>
              <FiMenu />
            </button>
            <DerasarSelector />
            {activeDerasarName && (
              <span className={styles.derasarName} title={activeDerasarName}>
                <FiHome className={styles.derasarNameIcon} />
                <span className={styles.derasarNameText}>{activeDerasarName}</span>
              </span>
            )}
          </div>
          <div className={styles.topbarRight}>
            <FinancialYearSelector />
            <NotificationBell />
            <ThemeSelector />

            {/* Avatar dropdown */}
            <div className={styles.avatarWrap} ref={avatarRef}>
              <button
                className={styles.avatarBtn}
                onClick={() => setAvatarOpen(o => !o)}
                aria-label="User menu"
              >
                {getInitials(user?.name)}
              </button>

              {avatarOpen && (
                <div className={styles.avatarDropdown}>
                  <div className={styles.avatarHeader}>
                    <span className={styles.avatarName}>{user?.name}</span>
                    <span className={styles.avatarRole}>{roleLabelMap[user?.role] || user?.role}</span>
                  </div>
                  <div className={styles.avatarDivider} />
                  <button className={styles.avatarItem} onClick={handleProfile}>
                    <FiUser /> My Profile
                  </button>
                  <button className={styles.avatarItem} onClick={handleLogout}>
                    <FiLogOut /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        {isActiveDerasarExpired && (
          <div className={styles.readOnlyBanner}>
            <div className={styles.readOnlyText}>
              <FiAlertTriangle size={16} />
              <span>
                Subscription expired{activeDerasarSubscriptionEndDate ? ` on ${new Date(activeDerasarSubscriptionEndDate).toLocaleDateString('en-IN')}` : ''}. This derasar is currently read-only.
              </span>
            </div>
            {isDerasarAdmin && !isActiveDerasarTrial && (
              activeDerasarHasPendingRenewal ? (
                <span className={styles.pendingPill}>Renewal payment submitted. Awaiting super admin approval.</span>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/subscription/renew')}>
                  <FiRefreshCw size={14} /> Renew Subscription
                </button>
              )
            )}
          </div>
        )}
        {!isActiveDerasarExpired && isActiveDerasarExpiringSoon && isDerasarAdmin && !activeDerasarHasPendingRenewal && !isActiveDerasarTrial && (
          <div className={styles.expiringBanner}>
            <div className={styles.readOnlyText}>
              <FiAlertTriangle size={16} />
              <span>
                Subscription expires in {activeDerasarDaysUntilExpiry} day{activeDerasarDaysUntilExpiry !== 1 ? 's' : ''}{activeDerasarSubscriptionEndDate ? ` on ${new Date(activeDerasarSubscriptionEndDate).toLocaleDateString('en-IN')}` : ''}. You can renew now and the next period will continue from your current end date.
              </span>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/subscription/renew')}>
              <FiRefreshCw size={14} /> Renew Now
            </button>
          </div>
        )}
        <div className={styles.pageContent}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
